// Helper functions
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";
import { getValueOnProperty } from "@/Common/helpers/objects";
import { isNumber, get as lodashGet, set as lodashSet } from "lodash";

// Utility functions
const getContentType = (content) => (Array.isArray(content) ? "LIST" : "OBJECT");
const isAdding = (current = [], previous = []) => Boolean(current?.length >= previous?.length);

const hasSimilarContentTypes = (contents = []) => {
	const compare = getContentType(contents?.[0]);
	return contents?.every((item) => compare === getContentType(item));
};

const hasSameRowsLength = (list = [], key = null) => {
	const compareLength = list?.[0]?.[key]?.length || list?.[0]?.length || 0;
	return list.every((element) => {
		const currentLength = element?.[key]?.length || element.length;
		return currentLength === compareLength;
	});
};

const hasSameColumnsLength = (list = [], key = null, columnsKey = null) => {
	if (columnsKey) {
		return list.every((element) => {
			const columnsOn = element?.[key] || element || [];
			return hasSameRowsLength(columnsOn, columnsKey);
		});
	}
	return false;
};

const convertToRegularList = (content) => {
	if (!content) {
		return [];
	}
	// eslint-disable-next-line no-unused-vars
	return Object.entries(content).map(([_, item]) => item);
};

const isSameListsDimensions = (lists = [], rowsKey = null, columnsKey = null) => {
	if (!lists.length) {
		return true;
	}
	const rows = hasSameRowsLength(lists, rowsKey);
	let columns = true;
	if (columnsKey) {
		columns = hasSameColumnsLength(lists, rowsKey, columnsKey);
	}
	return rows && columns;
};

const isSameDimension = (content, rowsKey = null, columnsKey = null) => {
	if (content) {
		const analyze = clone(content);
		const contentType = getContentType(analyze);
		switch (contentType) {
			case "LIST": {
				return isSameListsDimensions([...analyze], rowsKey, columnsKey);
			}
			case "OBJECT": {
				const list = convertToRegularList(analyze, rowsKey, columnsKey);
				return isSameListsDimensions([...list], rowsKey, columnsKey);
			}
			default:
				break;
		}
	}
	return true;
};

const getListContent = (content, key, index = 0) => {
	const contentType = getContentType(content);
	switch (contentType) {
		case "LIST":
			return key ? content?.[index]?.[key] : content?.[index];
		case "OBJECT":
			return key ? content?.[key] : content;
		default:
			break;
	}
	return [];
};

const hasAddedColumns = (row, previousRow, columnsKey = null) => {
	if (columnsKey) {
		const column = getListContent(row, columnsKey);
		const previousColumn = getListContent(previousRow, columnsKey);
		return isAdding(column, previousColumn);
	}
	return false;
};

const isAddingContent = (content, previousContent, rowsKey = null, columnsKey = null) => {
	let isAddingRows = false;
	let isAddingColumns = false;
	const current = clone(content);
	const previous = clone(previousContent);

	if (current && previous) {
		// Get content types
		const isSimilarTypes = hasSimilarContentTypes([current, previous]);
		const contentType = getContentType(current);

		if (isSimilarTypes) {
			let row = [];
			let previousRow = [];
			switch (contentType) {
				case "LIST": {
					row = getListContent(current?.[0], rowsKey);
					previousRow = getListContent(previous?.[0], rowsKey);
					break;
				}
				case "OBJECT": {
					row = getListContent(current, rowsKey);
					previousRow = getListContent(previous, rowsKey);
					break;
				}
				default:
					break;
			}

			isAddingRows = isAdding(row, previousRow);
			if (columnsKey) {
				isAddingColumns = hasAddedColumns(row, previousRow, columnsKey);
			}
		}
	}
	return { isAddingRows, isAddingColumns };
};

const getLimitsValues = (input, lessPossible = false) => {
	let min = null;
	let max = null;
	const MAX_INPUT = 1000000;
	const MAX_DURATION = 744;
	const MAX_PERCENTAGE = 100;

	switch (input) {
		case "Unit":
		case "Dollar":
		case "Dollar per Unit":
			min = 0;
			max = MAX_INPUT;
			if (lessPossible) {
				min = -MAX_INPUT;
			}
			break;
		case "Percent":
		case "Percent per Unit":
			min = 0;
			max = MAX_PERCENTAGE;
			if (lessPossible) {
				min = -MAX_PERCENTAGE;
			}
			break;
		case "Ordinal":
			min = 1;
			max = MAX_INPUT;
			break;
		case "Duration":
			min = 0;
			max = MAX_DURATION;
			break;
		default:
			break;
	}

	return { min, max };
};

const getSymmetricList = (lists = [], onKey = null) => {
	let symmetricList = [];
	if (!lists?.length) return [];
	[...lists].forEach((element) => {
		if (onKey) {
			symmetricList.push({
				[onKey]: element?.[onKey],
			});
		} else {
			symmetricList.push(element);
		}
	});
	return symmetricList;
};

const getListToDimension = (content, onKey = null) => {
	let listToDimension = [];
	const contentType = getContentType(content);
	switch (contentType) {
		case "LIST":
			listToDimension = getSymmetricList([...content], onKey);
			break;
		case "OBJECT": {
			if (content) {
				// eslint-disable-next-line no-unused-vars
				const lists = Object.entries(content)?.map(([_, element]) => element);
				listToDimension = getSymmetricList([...lists], onKey);
			}
			break;
		}
		default:
			break;
	}
	return listToDimension;
};

const getListMinMaxValues = (list = []) => {
	let min = 0;
	let max = 0;

	if (list?.length) {
		min = Math.min(Number.MAX_VALUE, list.length);
		max = Math.max(Number.MIN_VALUE, list.length);
	}

	return {
		min,
		max,
	};
};

const getColumns = (rows = [], onColumnKey = null) => {
	let columns = [];
	if (!rows.length || !onColumnKey) {
		return [];
	}
	if (Array.isArray(rows?.[0]?.[onColumnKey])) {
		columns = [...rows[0][onColumnKey]];
	}
	return columns;
};

const getSymmetricDimensions = (listToDimension = [], onKey = null, onColumnKey = null) => {
	let minRows = Number.MAX_VALUE,
		minColumns = Number.MAX_VALUE;
	let maxRows = 0,
		maxColumns = 0;

	if (!listToDimension?.length) {
		return {
			minRows: 0,
			minColumns: 0,
			maxRows: 0,
			maxColumns: 0,
		};
	}

	listToDimension.forEach((tier) => {
		const rows = onKey ? tier?.[onKey] : tier;
		const columnsList = getColumns(rows, onColumnKey);

		// Get min/max list values
		const { min, max } = getListMinMaxValues(rows);
		const { min: minColumn, max: maxColumn } = getListMinMaxValues(columnsList);

		// Compute the right dimensions
		minRows = Math.min(minRows, min); // Min rows (tiers) value
		maxRows = Math.max(maxRows, max); // Max rows (tiers) value
		minColumns = Math.min(minColumns, minColumn); // Min columns (if appear) value
		maxColumns = Math.max(maxColumns, maxColumn); // Max columns (if appear) value
	});

	return {
		minRows,
		minColumns,
		maxRows,
		maxColumns,
	};
};

const getDimensions = (content, onKey = null, onColumnKey = null) => {
	const listToDimension = getListToDimension(content, onKey);
	return getSymmetricDimensions(listToDimension, onKey, onColumnKey);
};

const getDifference = (currentLength, desiredLength) => desiredLength - currentLength;

/**
 * @description Fn to add or remove elements from an array or list having the dimension specified and the difference
 */
const applyDifference = (tier = [], dimension = 0, difference = 0) => {
	if (difference && difference < 0) {
		tier.length = dimension;
	} else if (difference && tier.length) {
		Array.from(Array(difference)).forEach(() => {
			const last = tier.length - 1;
			const add = clone(tier?.[last]);
			tier.push(add);
		});
	}
	return [...tier];
};

/**
 * @description Fn to synchronize an element inside a list (or array of elements)
 */
const syncElement = (item, dimensions = { rows: 0, columns: 0 }, rowsKey = null, columnsKey = null) => {
	if (item) {
		let rows = rowsKey ? clone(item?.[rowsKey]) : clone(item);
		const rowsDifference = getDifference(rows.length, dimensions.rows); // Computing rows difference
		// Applying difference to rows
		rows = applyDifference(rows, dimensions.rows, rowsDifference);

		// Go over defined columns
		if (columnsKey) {
			rows?.forEach((row) => {
				if (Array.isArray(row?.[columnsKey])) {
					let columns = [...row[columnsKey]];
					const columnsDifference = getDifference(columns.length, dimensions.columns); // Computing columns difference
					// Applying difference to columns
					columns = applyDifference(columns, dimensions.columns, columnsDifference);
					row[columnsKey] = [...columns];
				}
			});
		}

		if (rowsKey) {
			if (item?.[rowsKey]) {
				item[rowsKey] = rows;
			}
		} else {
			item = rows;
		}
	}
	return item;
};

/**
 * @description Fn to modify a value by reference having the object path (key) and the new value
 * @param element Any
 * @param key String
 * @param value Any
 * @returns Any
 */
const modifySelection = (element, key = null, value = null) => {
	if (!key) {
		return value;
	}
	if (element) {
		if (key.includes(".")) {
			const useKey = key.split(".");
			element[useKey[0]] = modifySelection(element[useKey[0]], useKey.slice(1).join("."), value);
		} else if (element?.[key]) {
			element[key] = value;
		}
	}
	return element;
};

const getModifierValue = (element, modifier, isLastElement, applyOnLast = "max", applyDefault = "min") => {
	let limits = null;
	if (modifier?.useLimitReferenceOn) {
		const reference = getValueOnProperty(modifier.useLimitReferenceOn, element);
		limits = getLimitsValues(reference, Boolean(modifier?.useLessPossible));
	}

	const { min, max } = limits || {};

	// Here always apply the 'min' value by default
	let valueToApply = min || modifier?.[applyDefault];

	if (isLastElement) {
		// Apply the 'max' value on last element
		valueToApply = max || modifier?.[applyOnLast];
	}

	return valueToApply || null;
};

const modifyElementValues = (
	element,
	values = [],
	isLastElement = false,
	keepNotReduce = false,
	applyOnLast = "max",
	applyDefault = "min"
) => {
	if (!values?.length) {
		return element;
	}

	values.forEach((definition) => {
		if (definition) {
			const entries = Object.entries(definition);
			const [key, modifier] = entries.length > 0 ? entries[0] : [];
			const valueToApply = getModifierValue(element, modifier, isLastElement, applyOnLast, applyDefault);

			// Condition to not modify the element
			const notReduce = Boolean(modifier?.notReduce) && keepNotReduce;

			if (!notReduce) {
				element = modifySelection(element, key, valueToApply);
			}
		}
	});

	return element;
};

const getUsableConditions = (values = []) =>
	values
		?.filter((definition) => {
			if (definition) {
				const entries = Object.entries(definition);
				const [, modifier] = entries?.[0] || [];
				return Boolean(modifier?.conditions);
			}
			return false;
		})
		.map((definition) => {
			const entries = Object.entries(definition);
			const [key, modifier] = entries?.[0] || [];
			return {
				modify: key,
				...modifier,
			};
		});

const getConditionValue = (condition) => {
	const { value, tiers = [], index = 0, fromPrevious = false, delta = 0 } = condition;
	if (isNumber(value)) {
		return value + delta;
	}
	const indexReference = fromPrevious ? index - 1 : index;
	const recoveredValue = lodashGet(tiers[indexReference], value, 0);
	return recoveredValue + delta;
};

const applyConditionsValues = (args) => {
	const { modify, tiers = [], conditions = [], index = 0, adding = false } = args;
	if (!modify || !tiers?.length || !conditions?.length) {
		return;
	}

	const actionConditions = conditions.filter((condition) => Boolean(condition?.isAdding) === adding);

	for (const condition of actionConditions) {
		const { limit = "", onBefore = false } = condition || {};
		const indexReference = onBefore ? index - 1 : index;
		let value;
		if (limit) {
			value = args?.[limit] || 0;
		} else {
			value = getConditionValue({ ...condition, tiers, index });
		}
		lodashSet(tiers[indexReference], modify, value);
	}
};

const applyConditions = (args) => {
	const { tiers = [], values = [], index = 0, isLastElement = false, adding = false } = args;

	if (!tiers?.length || !values?.length || !isLastElement) {
		return;
	}

	const conditionsValues = getUsableConditions(values);

	for (const condition of conditionsValues) {
		applyConditionsValues({ ...condition, tiers, index, adding });
	}
};

/**
 * @description Fn to fill elements from an array or list having the specified values
 */
const adjustListValues = (tiers = [], values = [], isAllFillable = false) => {
	if (tiers.length) {
		const originalTiers = clone(tiers);
		const maxLength = tiers.length - 1;
		tiers.forEach((element, index) => {
			const isLastElement = Boolean(maxLength === index);
			const previous = originalTiers?.[index - 1] || null;
			const current = originalTiers?.[index] || null;

			// The element only will be modified if shouldModifyValues
			const shouldModifyValues = isSimilarObject(previous, current) || (isLastElement && !isAllFillable);
			if (shouldModifyValues) {
				tiers[index] = modifyElementValues(element, values, isLastElement, !isAllFillable);
				applyConditions({ tiers, values, index, isLastElement, adding: isAllFillable });
			}
		});
	}
	return tiers;
};

/**
 * @description Fn to fill an element inside a list (or array of elements) using some provided values
 */
const fullfilElement = (item, rows, columns, isAllFillable = false) => {
	let result = clone(item);

	if (!result) {
		return result;
	}

	if (rows) {
		let fillRows = rows?.key ? result?.[rows.key] : result;

		// Applying values to rows
		fillRows = adjustListValues(fillRows, rows?.values, isAllFillable);

		// Go over defined columns
		if (columns?.key) {
			fillRows?.forEach((row) => {
				const fillColumns = clone(row?.[columns.key]);
				if (Array.isArray(fillColumns)) {
					// Applying values to columns
					row[columns.key] = adjustListValues(fillColumns, columns?.values, Boolean(isAllFillable));
				}
			});
		}

		if (rows?.key) {
			if (result?.[rows.key]) {
				result[rows.key] = fillRows;
			}
		} else {
			result = fillRows;
		}
	}

	return result;
};

const getSyncContent = (content, dimensions = { rows: 0, columns: 0 }, rowsKey = null, columnsKey = null) => {
	if (content) {
		const result = clone(content);
		const contentType = getContentType(content);
		switch (contentType) {
			case "LIST":
				result.forEach((item, index) => {
					result[index] = syncElement(item, dimensions, rowsKey, columnsKey);
				});
				break;
			case "OBJECT": {
				Object.entries(result).forEach(([key, item]) => {
					result[key] = syncElement(item, dimensions, rowsKey, columnsKey);
				});
				break;
			}
			default:
				break;
		}
		return result;
	}
	return content;
};

const syncTiers = (content, isAdding = false, rowsKey = null, columnsKey = null) => {
	const { minRows, minColumns, maxRows, maxColumns } = getDimensions(content, rowsKey, columnsKey);
	const dimensions = isAdding ? { rows: maxRows, columns: maxColumns } : { rows: minRows, columns: minColumns };
	return getSyncContent(content, dimensions, rowsKey, columnsKey);
};

const adjustTiersValues = (
	content,
	isAdding = false,
	rows = {
		key: null,
		values: [{ "from.value": { min: 0, max: 0 } }, { "to.value": { min: 0, max: 1000000 } }],
	},
	columns = {
		key: null,
		values: [
			{ "from.value": { min: 0, max: 0 } },
			{ "to.value": { min: 0, max: 1000000 } },
			{ "commission.value": { min: 0, max: 0 } },
		],
	}
) => {
	const result = clone(content);

	if (result) {
		const contentType = getContentType(result);
		switch (contentType) {
			case "LIST":
				result.forEach((item, index) => {
					result[index] = fullfilElement(item, rows, columns, isAdding);
				});
				break;
			case "OBJECT": {
				Object.entries(result).forEach(([key, item]) => {
					result[key] = fullfilElement(item, rows, columns, isAdding);
				});
				break;
			}
			default:
				break;
		}
	}
	return result;
};

// let test = {
// 	new: {
// 		tiers: [
// 			{ from: { value: 0, input: "Percent" }, to: { value: 5, input: "Percent" } },
// 			{ from: { value: 5, input: "Percent" }, to: { value: 100, input: "Percent" } },
// 		],
// 	},
// 	preOwned: {
// 		tiers: [{ from: { value: 0, input: "Percent" }, to: { value: 35, input: "Percent" } }],
// 	},
// };

// let testA = {
// 	new: [{ from: { value: 0, input: "Percent" }, to: { value: 100, input: "Percent" } }],
// 	preOwned: [
// 		{ from: { value: 0, input: "Percent" }, to: { value: 5, input: "Percent" } },
// 		{ from: { value: 0, input: "Percent" }, to: { value: 100, input: "Percent" } },
// 	],
// };

// let testB = [
// 	{
// 		make: "a",
// 		tiers: [
// 			{
// 				from: { value: 0, input: "Percent" },
// 				to: { value: 1, input: "Percent" },
// 				bonuses: [
// 					{
// 						from: { value: 0, input: "Percent" },
// 						to: { value: 100, input: "Percent" },
// 						bonus: { value: 100, input: "Dollar" },
// 					},
// 				],
// 			},
// 		],
// 	},
// 	{
// 		make: "b",
// 		tiers: [
// 			{
// 				from: { value: 0, input: "Percent" },
// 				to: { value: 1, input: "Percent" },
// 				bonuses: [
// 					{
// 						from: { value: 0, input: "Percent" },
// 						to: { value: 50, input: "Percent" },
// 						bonus: { value: 20, input: "Dollar" },
// 					},
// 					{
// 						from: { value: 50, input: "Percent" },
// 						to: { value: 100, input: "Percent" },
// 						bonus: { value: 30, input: "Dollar" },
// 					},
// 				],
// 			},
// 			{
// 				from: { value: 1, input: "Percent" },
// 				to: { value: 5, input: "Percent" },
// 				bonuses: [
// 					{
// 						from: { value: 0, input: "Percent" },
// 						to: { value: 50, input: "Percent" },
// 						bonus: { value: 40, input: "Dollar" },
// 					},
// 					{
// 						from: { value: 50, input: "Percent" },
// 						to: { value: 100, input: "Percent" },
// 						bonus: { value: 50, input: "Dollar" },
// 					},
// 				],
// 			},
// 			{
// 				from: { value: 5, input: "Percent" },
// 				to: { value: 100, input: "Percent" },
// 				bonuses: [
// 					{
// 						from: { value: 0, input: "Percent" },
// 						to: { value: 50, input: "Percent" },
// 						bonus: { value: 50, input: "Dollar" },
// 					},
// 					{
// 						from: { value: 50, input: "Percent" },
// 						to: { value: 100, input: "Percent" },
// 						bonus: { value: 60, input: "Dollar" },
// 					},
// 				],
// 			},
// 		],
// 	},
// ];

// let adding = false;

// let syncTest = syncTiers(test, adding, "tiers");
// let adjustTest = adjustTiersValues(syncTest, adding, {
// 	key: "tiers",
// 	values: [{ "from.value": { min: 0, max: 0 } }, { "to.value": { min: 0, max: 100 } }],
// });
// console.log("test", test);
// console.log("adjustTest", JSON.stringify(adjustTest));

// let syncTestA = syncTiers(testA, adding);
// let adjustTestA = adjustTiersValues(syncTestA, adding, {
// 	key: null,
// 	values: [{ "from.value": { min: 0, max: 0 } }, { "to.value": { min: 0, max: 100 } }],
// });
// console.log("testA", testA);
// console.log("adjustTestA", JSON.stringify(adjustTestA));

// console.log(syncTiers(testA, true));
// let syncB = syncTiers(testB, adding, "tiers", "bonuses");
// let adjustB = adjustTiersValues(
// 	syncB,
// 	adding,
// 	{
// 		key: "tiers",
// 		values: [{ "from.value": { min: 0, max: 0 } }, { "to.value": { min: 0, max: 100 } }],
// 	},
// 	{
// 		key: "bonuses",
// 		values: [
// 			{ "from.value": { min: 0, max: 0 } },
// 			{ "to.value": { min: 0, max: 100 } },
// 			{ "bonus.value": { min: 0, max: 0, notReduce: true } },
// 		],
// 	}
// );
// console.log("testB", testB);
// console.log("adjustB", JSON.stringify(adjustB));

const mirrorAttributes = (fromContent, toContent, values = []) => {
	const result = clone(toContent);
	if (fromContent && result && values?.length) {
		for (const attribute of values) {
			if (attribute && attribute in result) {
				const definition = getValueOnProperty(attribute, fromContent);
				result[attribute] = clone(definition);
			}
		}
	}
	return result;
};

const setMirrorFrom = (copyFrom, copyTo, rows, columns) => {
	const isAvailableToMirror = hasSimilarContentTypes([copyFrom, copyTo]);
	if (isAvailableToMirror) {
		const result = clone(copyTo);
		const contentType = getContentType(copyFrom);

		switch (contentType) {
			case "LIST": {
				for (let i = 0; i < copyFrom?.length; i++) {
					const row = getListContent(result?.[i], rows?.key);
					if (row) {
						result[i] = mirrorAttributes(copyFrom[i], row, rows?.values);

						if (columns?.key && Boolean(columns?.values?.length)) {
							result[i] = setMirrorFrom(copyFrom[i], result[i], columns);
						}
					}
				}
				break;
			}
			case "OBJECT": {
				if (rows?.key) {
					const listRowsFrom = getListContent(copyFrom, rows?.key);
					const listRowsTo = getListContent(result, rows?.key);
					if (listRowsFrom.length === listRowsTo.length) {
						for (let i = 0; i < listRowsFrom?.length; i++) {
							listRowsTo[i] = mirrorAttributes(listRowsFrom[i], listRowsTo[i], rows?.values);

							if (columns?.key && Boolean(columns?.values?.length)) {
								listRowsTo[i] = setMirrorFrom(listRowsFrom[i], listRowsTo[i], columns);
							}
						}
						result[rows?.key] = [...listRowsTo];
					}
				}
				break;
			}
			default:
				break;
		}
		return result;
	}

	return copyTo;
};

const getContentToUseOnMirror = (content, index = 0) => content?.[index];

const mapMirrorItem = (item, index, copyFrom, indexFrom, rows, columns, indexesToAvoid = []) => {
	if (index !== indexFrom && !indexesToAvoid.includes(index)) {
		return setMirrorFrom(copyFrom, item, rows, columns);
	}
	return item;
};

/**
 * @description Fn to mirror contents.
 * This works on the base that the contents have the same length on the elements you want to mirror.
 */
const mirrorContent = (
	content,
	indexFrom = 0,
	rows = { key: null, values: [] },
	columns = { key: null, values: [] },
	indexesToAvoid = []
) => {
	const contentType = getContentType(content);
	const copyFrom = getContentToUseOnMirror(content, indexFrom);

	switch (contentType) {
		case "LIST": {
			return content?.map((item, index) => mapMirrorItem(item, index, copyFrom, indexFrom, rows, columns, indexesToAvoid));
		}
		case "OBJECT": {
			return Object.entries(content).map(([index, item]) => ({
				[index]: mapMirrorItem(item, index, copyFrom, indexFrom, rows, columns, indexesToAvoid),
			}));
		}
		default:
			break;
	}

	return content;
};

// let test = {
// 	new: {
// 		tiers: [
// 			{ from: { value: 10, input: "Dollar" } },
// 			{ from: { value: 10, input: "Dollar" } },
// 			{ from: { value: 10, input: "Dollar" } },
// 		],
// 	},
// 	preOwned: {
// 		tiers: [
// 			{ from: { value: 5, input: "Percent" } },
// 			{ from: { value: 5, input: "Percent" } },
// 			{ from: { value: 5, input: "Percent" } },
// 		],
// 	},
// };

// let test = {
// 	new: [
// 		{ from: { value: 1, input: "Dollar" }, bonuses: [{ bonus: { value: 5, input: "Unit" } }] },
// 		{ bonuses: [{ bonus: { value: 5, input: "Unit" } }] },
// 		{ bonuses: [{ bonus: { value: 5, input: "Unit" } }] },
// 	],
// 	preOwned: [
// 		{ from: { value: 5, input: "Percent" }, bonuses: [{ bonus: { value: 15, input: "Unit" } }] },
// 		{ bonuses: [{ bonus: { value: 15, input: "Unit" } }] },
// 		{ bonuses: [{ bonus: { value: 15, input: "Unit" } }] },
// 	],
// };

// let test = [
// 	{
// 		make: "Chevy",
// 		tiers: [
// 			{
// 				from: { value: 5, input: "Dollar" },
// 				bonuses: [
// 					{ from: { value: 5, input: "Percent" }, bonus: { value: 5, input: "Unit" } },
// 					{ from: { value: 5, input: "Percent" }, bonus: { value: 17, input: "Unit" } },
// 				],
// 			},
// 			{
// 				from: { value: 5, input: "Dollar" },
// 				bonuses: [
// 					{ from: { value: 5, input: "Percent" }, bonus: { value: 5, input: "Unit" } },
// 					{ from: { value: 5, input: "Percent" }, bonus: { value: 17, input: "Unit" } },
// 				],
// 			},
// 			{
// 				from: { value: 5, input: "Dollar" },
// 				bonuses: [
// 					{ from: { value: 5, input: "Percent" }, bonus: { value: 5, input: "Unit" } },
// 					{ from: { value: 5, input: "Percent" }, bonus: { value: 17, input: "Unit" } },
// 				],
// 			},
// 		],
// 	},
// 	{
// 		make: "Mitsubishi",
// 		tiers: [
// 			{
// 				from: { value: 1, input: "Dollar" },
// 				bonuses: [
// 					{ from: { value: 1, input: "Percent" }, bonus: { value: 15, input: "Unit" } },
// 					{ from: { value: 1, input: "Percent" }, bonus: { value: 15, input: "Unit" } },
// 				],
// 			},
// 			{
// 				from: { value: 1, input: "Dollar" },
// 				bonuses: [
// 					{ from: { value: 1, input: "Percent" }, bonus: { value: 15, input: "Unit" } },
// 					{ from: { value: 1, input: "Percent" }, bonus: { value: 15, input: "Unit" } },
// 				],
// 			},
// 			{
// 				from: { value: 1, input: "Dollar" },
// 				bonuses: [
// 					{ from: { value: 1, input: "Percent" }, bonus: { value: 15, input: "Unit" } },
// 					{ from: { value: 1, input: "Percent" }, bonus: { value: 15, input: "Unit" } },
// 				],
// 			},
// 		],
// 	},
// ];

// const result = mirrorContent(test, "new", { key: "tiers", values: [] }, { key: "bonuses", values: [] });
// const result = mirrorContent(test, "new", { key: null, values: [] }, { key: "bonuses", values: ["bonus"] });
// const result = mirrorContent(test, 0, { key: "tiers", values: [] }, { key: "bonuses", values: ["bonus"] });
// console.log("result", JSON.stringify(result));

export { adjustTiersValues, getLimitsValues, getDimensions, isAdding, isAddingContent, isSameDimension, mirrorContent, syncTiers };
